import {
  CallToAction,
  OfferBanner,
  OfferBannerConfig,
  OfferBannerStyles
} from '@com-delta-idp/mnx-fast-action-banner-ui';
import { Banner } from '@com-delta-idp/mnx-skymiles-overview-ui';
import {
  AEMBannerConfig,
  AEMBannerStyles,
  AEMErrorMessage,
  AEMFastActionBanner,
  OfferAmexBanner,
  UserSession,
  checkDefined,
  checkErrors,
  getEmptyContent,
  getImage,
  getLink,
  isDisplay,
  isError,
  isValidData
} from '@com-delta-idp/mnx-skymiles-shared';
import { logError } from '@com-delta-idp/mnx-utils';
import { MultiplePersonalizationEngineResponse } from '../models';
import { getBannerStyle } from '../utils/banner.utils';

export const getBanner = (
  aem: AEMFastActionBanner | undefined,
  pe: MultiplePersonalizationEngineResponse | undefined,
  profile: UserSession | undefined,
  featureFlag: boolean | undefined,
  errors: (string | undefined)[],
  aemErrorMessages: AEMErrorMessage[] | undefined
): Banner => {
  try {
    if (checkErrors(errors, aemErrorMessages)) {
      return isError<Banner>(
        'banner',
        aemErrorMessages as AEMErrorMessage[],
        featureFlag
      );
    }
    return isValidData<
      AEMFastActionBanner,
      [
        AEMFastActionBanner | undefined,
        MultiplePersonalizationEngineResponse | undefined,
        UserSession | undefined,
        boolean | undefined
      ]
    >('offerConfigs', aem, pe, profile, featureFlag)
      ? adaptStaticBanner(
          aem as AEMFastActionBanner,
          pe as MultiplePersonalizationEngineResponse,
          profile as UserSession,
          featureFlag as boolean
        )
      : isDisplay<Banner>(featureFlag);
  } catch (error) {
    logError(error as Error, 'SKYMILES', 'OVERVIEW', 'BANNER');
    return getEmptyContent<Banner>('banner', aemErrorMessages, true);
  }
};

const adaptStaticBanner = (
  aem: AEMFastActionBanner,
  pe: MultiplePersonalizationEngineResponse,
  profile: UserSession,
  featureFlag: boolean
): Banner => {
  if (pe['ng-skymiles:amex-banners'] && featureFlag) {
    const amexStaticBanner: OfferAmexBanner =
      pe['ng-skymiles:amex-banners'].parsys.amexbanner;
    return {
      offer: getOffer(amexStaticBanner),
      offerConfig: getOfferConfig(aem.offerConfigs),
      offerStyles: getOfferStyles(aem.offerStyles, profile.membershipLevel),
      disabled: !featureFlag,
      loaded: true
    };
  } else {
    return getEmptyContent<Banner>('', [], true, true);
  }
};

const getOffer = (amexStaticBanner: OfferAmexBanner): OfferBanner => {
  return {
    action: getAction(amexStaticBanner),
    offer: amexStaticBanner.title,
    benefit: amexStaticBanner.description,
    headline: amexStaticBanner.headline,
    image: getImage(amexStaticBanner.cardImage, amexStaticBanner.description),
    cardType: amexStaticBanner.cardType
  };
};

const getAction = (amexStaticBanner: OfferAmexBanner): CallToAction => {
  return {
    input: 'button',
    link: getLink(
      amexStaticBanner.learnMoreLabel,
      amexStaticBanner.learnMoreCTA
    ),
    linkId: amexStaticBanner.learnMoreId + '-' + amexStaticBanner.cardType,
    secondaryLink: getLink(
      amexStaticBanner.termsLabel,
      amexStaticBanner.termsCTA
    ),
    secondaryLinkId: amexStaticBanner.termsId + '-' + amexStaticBanner.cardType
  };
};
const getOfferConfig = (bannerConfig: AEMBannerConfig): OfferBannerConfig => {
  return {
    congratulation: JSON.parse(checkDefined(bannerConfig.congratulation)),
    showAction: JSON.parse(checkDefined(bannerConfig.showAction)),
    showBenefit: JSON.parse(checkDefined(bannerConfig.showBenefit)),
    showCreditSummary: JSON.parse(checkDefined(bannerConfig.showCreditSummary)),
    showDetails: JSON.parse(checkDefined(bannerConfig.showDetails)),
    showHeadline: JSON.parse(checkDefined(bannerConfig.showHeadline)),
    showLinks: JSON.parse(checkDefined(bannerConfig.showLinks)),
    showOffer: JSON.parse(checkDefined(bannerConfig.showOffer))
  };
};

const getOfferStyles = (
  bannerStyles: AEMBannerStyles,
  membershipLevel: string
): OfferBannerStyles => {
  return {
    offer: getBannerStyle(bannerStyles, membershipLevel),
    creditSummary: bannerStyles.creditSummary,
    welcome: bannerStyles.welcome
  };
};
